import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ControlTemplate } from '@/lib/control-templates';
import {
  Zap, ZapOff, ShieldPlus, ShieldMinus, CloudLightning,
  Home, ArrowUpFromLine, Sun, Settings2, Battery,
} from 'lucide-react';

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Zap, ZapOff, ShieldPlus, ShieldMinus, CloudLightning,
  Home, ArrowUpFromLine, Sun, Settings2, Battery,
};

const RISK_STYLES: Record<string, { badge: string; label: string }> = {
  low: { badge: 'bg-wt-green-primary/10 text-wt-green-primary border-wt-green-primary/20', label: 'Low risk' },
  medium: { badge: 'bg-wt-amber-primary/10 text-wt-amber-primary border-wt-amber-primary/20', label: 'Medium risk' },
  high: { badge: 'bg-wt-red-primary/10 text-wt-red-primary border-wt-red-primary/20', label: 'High risk' },
};

interface ActionCardProps {
  template: ControlTemplate;
  disabled?: boolean;
  onClick: (template: ControlTemplate) => void;
}

export function ActionCard({ template, disabled, onClick }: ActionCardProps) {
  const Icon = ICON_MAP[template.icon] ?? Settings2;
  const risk = RISK_STYLES[template.risk_level] ?? RISK_STYLES.low;

  return (
    <button
      onClick={() => !disabled && onClick(template)}
      disabled={disabled}
      className={cn(
        'w-full text-left rounded-lg border p-4 transition-colors',
        'border-wt-border-subtle bg-wt-bg-secondary',
        disabled
          ? 'opacity-50 cursor-not-allowed'
          : 'hover:border-wt-border-default hover:bg-wt-bg-tertiary/50 cursor-pointer',
      )}
    >
      <div className="flex items-start gap-3">
        <div className={cn(
          'flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-md',
          'bg-wt-bg-primary border border-wt-border-subtle',
        )}>
          <Icon className="h-4 w-4 text-wt-text-secondary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-wt-text-primary truncate">
              {template.name}
            </span>
            <Badge
              variant="outline"
              className={cn('text-[10px] px-1.5 py-0 h-4 border', risk.badge)}
            >
              {risk.label}
            </Badge>
          </div>
          <p className="text-xs text-wt-text-muted leading-relaxed">
            {template.description}
          </p>
        </div>
      </div>
    </button>
  );
}

export function ActionCardSkeleton() {
  return (
    <div className="rounded-lg border border-wt-border-subtle bg-wt-bg-secondary p-4 animate-pulse">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-md bg-wt-bg-primary" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-32 bg-wt-bg-primary rounded" />
          <div className="h-3 w-full bg-wt-bg-primary rounded" />
        </div>
      </div>
    </div>
  );
}
